import { useEffect, useRef, RefObject } from "react";

interface UseScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

// Detect mobile/low-power devices to reduce animation intensity.
const isMobile = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(max-width: 768px)").matches ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches);

export function useScrollAnimation(
  options: UseScrollAnimationOptions = {}
): RefObject<HTMLDivElement> {
  // On mobile use a smaller threshold so elements animate in earlier
  // (before they're fully on-screen) and a less aggressive rootMargin.
  const mobile = isMobile();
  const {
    threshold = mobile ? 0.05 : 0.15,
    rootMargin = mobile ? "0px 0px -20px 0px" : "0px 0px -60px 0px",
    once = true,
  } = options;

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect prefers-reduced-motion — skip animation entirely.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const animatables = el.querySelectorAll<HTMLElement>(".animate-on-scroll");
      animatables.forEach((child) => child.classList.add("in-view"));
      if (el.classList.contains("animate-on-scroll")) el.classList.add("in-view");
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            if (once) observer.unobserve(entry.target);

            // Release will-change after the transition ends to free GPU memory.
            const el = entry.target as HTMLElement;
            const cleanup = () => {
              el.classList.add("animation-done");
              el.removeEventListener("transitionend", cleanup);
            };
            el.addEventListener("transitionend", cleanup, { once: true });
          } else if (!once) {
            entry.target.classList.remove("in-view");
            entry.target.classList.remove("animation-done");
          }
        });
      },
      { threshold, rootMargin }
    );

    // Observe the container and all direct children with animate-on-scroll
    const animatables = el.querySelectorAll<HTMLElement>(".animate-on-scroll");
    if (el.classList.contains("animate-on-scroll")) {
      observer.observe(el);
    }
    animatables.forEach((child) => observer.observe(child));

    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return ref;
}

// Stagger utility — wraps children with incremental delays.
// On mobile, use a smaller increment so the stagger isn't so stretched out.
export function staggerChildren(
  containerRef: RefObject<HTMLElement>,
  baseDelay = 0,
  increment = 100
): void {
  const container = containerRef.current;
  if (!container) return;
  const mobile = isMobile();
  const effectiveIncrement = mobile ? Math.round(increment * 0.5) : increment;
  const children = Array.from(
    container.querySelectorAll<HTMLElement>(".animate-on-scroll")
  );
  children.forEach((child, i) => {
    child.style.transitionDelay = `${baseDelay + i * effectiveIncrement}ms`;
  });
}
